export const sections = [
    {
        id: 'vertx',
        icon: '⚡',
        color: '#6366f1',
        bg: 'rgba(99,102,241,0.15)',
        title: 'Vert.x',
        tagline: 'Reactive Microservices',
        analogy: '🍕 Think of Vert.x like a single pizza chef who takes orders (requests) and never blocks — while the oven bakes (async IO), the chef takes the next order. Worker verticles are like a separate grill station for heavy jobs.',
        simple: 'Vert.x is a toolkit that lets your Java app handle thousands of connections at once without using thousands of threads. Traditional servers assign one thread per request (slow). Vert.x uses an Event Loop — one thread handles all I/O without waiting.',
        keyPoints: [
            'Event Loop thread: never block it — no Thread.sleep(), no blocking DB calls here',
            'Verticle: your code unit (like a small service). Standard = on Event Loop. Worker = on thread pool for blocking ops',
            'Event Bus: internal post office — verticles send messages to each other by address',
            'vs Spring MVC: Spring uses 1 thread per request. Vert.x uses 1 thread for thousands of requests',
            'Non-blocking means: instead of waiting for DB → register a callback → go handle next request',
        ],
        code: `// Java: Worker Verticle for blocking ops
public class ReportWorker extends AbstractVerticle {
  public void start() {
    vertx.eventBus().consumer("generate.report", msg -> {
      String pdf = pdfLib.generate(); // blocking is OK in Worker
      msg.reply(pdf);
    });
  }
}

# Python Equivalent:
# Python's asyncio is like the Vert.x Event Loop.
# For blocking ops, use run_in_executor (like a Worker Verticle).
import asyncio

async def handle_report(msg):
    # Run blocking PDF generation in a thread pool
    loop = asyncio.get_running_loop()
    pdf = await loop.run_in_executor(None, pdf_lib.generate)
    msg.reply(pdf)`,
        qa: [
            {
                q: 'Q: What happens if you block the Vert.x Event Loop?',
                a: `Simple answer: Everything freezes.\n\nImagine a phone operator handling 1000 calls. If they put ONE caller on infinite hold and never come back — nobody else gets answered either.\n\nVert.x detects this: if the event loop is blocked for > 2 seconds it logs a WARNING. Performance drops to near zero for all requests on that thread.\n\n✅ Fix: Move any blocking operation (file I/O, JDBC, heavy CPU) to a Worker Verticle using vertx.executeBlocking()`,
            },
            {
                q: 'Q: What are the 3 Event Bus patterns? Give a real example of each.',
                a: `1. Point-to-Point → "Order Service sends message to Inventory Service to check stock"\n   → Only ONE consumer gets the message (Vert.x round-robins if multiple consumers)\n\n2. Pub/Sub → "Order placed event is broadcast to Email Service, Analytics Service, and Audit Service"\n   → ALL subscribers receive the same message\n\n3. Request/Reply → "API Gateway asks Auth Service: is this token valid? Auth replies yes/no"\n   → Like a function call over the Event Bus — consumer must call msg.reply()`,
            },
            {
                q: 'Q: How does Vert.x compare to Spring Boot?',
                a: `Spring Boot: A full framework with auto-configuration, DI, security, ORM — opinionated and quick to start. One thread per HTTP request (thread-per-request model).\n\nVert.x: A toolkit — you pick what you need. Uses the Event Loop model. Much higher throughput for I/O-heavy workloads.\n\nBoth can be reactive:\n• Spring Boot + WebFlux = reactive Spring\n• Vert.x is reactive by default\n\nInterview answer: "I'd choose Vert.x for high-throughput event-driven microservices, Spring Boot for teams familiar with the Spring ecosystem and rapid CRUD service development."`,
            },
        ],
    },
    {
        id: 'springboot',
        icon: '🍃',
        color: '#22c55e',
        bg: 'rgba(34,197,94,0.15)',
        title: 'Spring Boot',
        tagline: 'RESTful Microservices',
        analogy: '🏗️ Spring Boot is like a pre-configured building kit. Instead of hand-laying every brick (XML config), it auto-configures based on what is in your toolbox (classpath). Add the JDBC jar and database config appears automatically.',
        simple: 'Spring Boot eliminates boilerplate by auto-detecting what your app needs (based on jars you include) and configuring it automatically. Build REST APIs, connect to databases, add security — with just annotations and minimal code.',
        keyPoints: [
            '@SpringBootApplication = 3 annotations in 1: @Configuration + @EnableAutoConfiguration + @ComponentScan',
            'Auto-configuration: adds a DataSource ONLY if you have a DB driver jar AND not already defined one',
            'Use constructor injection (not @Autowired field) — easier to test, fails fast at startup if missing',
            '@Profile("prod") loads prod beans, @Profile("dev") loads dev beans — no if/else in code',
            'Actuator exposes /health and /metrics — Kubernetes uses /health for liveness/readiness probes',
        ],
        code: `// Java: Spring Boot REST Controller
@RestController
@RequestMapping("/api/v1/orders")
public class OrderController {
  private final OrderService service;
  public OrderController(OrderService service) { this.service = service; }

  @GetMapping("/{id}")
  public ResponseEntity<OrderDTO> get(@PathVariable Long id) {
    return service.findById(id).map(ResponseEntity::ok).orElse(ResponseEntity.notFound().build());
  }
}

# Python Equivalent (FastAPI):
from fastapi import FastAPI, HTTPException
app = FastAPI()

@app.get("/api/v1/orders/{order_id}")
async def get_order(order_id: int):
    order = await service.find_by_id(order_id)
    if not order:
        raise HTTPException(status_code=404)
    return order`,
        qa: [
            {
                q: 'Q: How does Spring Boot auto-configuration work? Explain simply.',
                a: `Imagine Spring Boot reads a list of "recipes" at startup (spring.factories file).\n\nEach recipe says: "IF you have library X on your classpath AND you haven't already defined Y, THEN I'll create Y for you."\n\nExample:\n• You add spring-boot-starter-data-jpa to pom.xml\n• Spring detects Hibernate + DataSource jar on classpath\n• @ConditionalOnClass checks: "Is JPA available?" → YES\n• @ConditionalOnMissingBean checks: "Did you define your own EntityManager?" → NO\n• Spring auto-creates EntityManagerFactory, TransactionManager for you\n\nYou get database connectivity for free — zero XML.`,
            },
            {
                q: 'Q: Why prefer constructor injection over @Autowired on fields?',
                a: `3 reasons:\n\n1. TESTABILITY: With constructor injection you can create the class in a test with a mock:\n   new OrderService(mockRepository) — no Spring context needed\n   With field @Autowired you must spin up Spring to inject it.\n\n2. IMMUTABILITY: You can make the field final → the object is immutable after creation.\n\n3. FAIL FAST: Circular dependencies are detected at startup and throw an error — better to fail early than fail in production.\n\n"Circular dependency" = ClassA needs ClassB, ClassB needs ClassA.\nWith constructor injection, Spring can't build either one → startup fails immediately.`,
            },
            {
                q: 'Q: How do you design a resilient Spring Boot microservice?',
                a: `Layer your defenses (like an onion):\n\n1. Timeout — every external call has a deadline (5 seconds max)\n2. Retry — transient failures (network hiccup) retry with exponential backoff\n3. Circuit Breaker — if service X fails 5 times → "open circuit" → return cached/default response instantly for 30 seconds → try again\n4. Bulkhead — separate thread pools per downstream service so one slow service doesn't exhaust all threads\n5. Health check — /actuator/health tells Kubernetes to restart if unhealthy\n\nTool: Resilience4j — add @CircuitBreaker annotation to any method.\n\nSTAR answer: "We had Order service calling Inventory service. Inventory was slow. Circuit Breaker opened after 5 failures, returning cached stock levels. Orders continued flowing. Inventory recovered, circuit closed. Zero downtime."`,
                star: true,
            },
        ],
    },
    {
        id: 'kafka',
        icon: '📨',
        color: '#f59e0b',
        bg: 'rgba(245,158,11,0.15)',
        title: 'Apache Kafka',
        tagline: 'Event Streaming Platform',
        analogy: '📰 Kafka is like a newspaper printing press. Publishers (producers) print stories (events) into sections (topics). The printed papers are kept on a shelf (log) for days. Multiple subscriber groups (consumer groups) each read their own copy at their own pace — the paper isn\'t "consumed" and disappears like a queue.',
        simple: 'Kafka stores events in order, forever (configurable). Unlike a message queue (RabbitMQ) where messages disappear after consumption, Kafka keeps them — so multiple services can read the same event stream independently.',
        keyPoints: [
            'Topic = a named channel (e.g. "orders"). Divided into Partitions for parallelism',
            'Partition = ordered, immutable log. Messages inside a partition are always in order',
            'Consumer Group = team of consumers. Each partition is owned by EXACTLY ONE consumer in the group',
            'Offset = bookmark — where a consumer stopped reading. Kafka remembers this per group',
            'More partitions = more parallel consumers = higher throughput (but more overhead)',
        ],
        code: `// Java: Producer
kafka.send("orders.placed", orderId, new OrderEvent(order));

// Java: Consumer
@KafkaListener(topics = "orders.placed", groupId = "inventory-service")
public void reserveStock(List<OrderEvent> events, Acknowledgment ack) {
  events.forEach(e -> inventory.reserve(e.getItems()));
  ack.acknowledge();
}

# Python Equivalent (confluent_kafka):
from confluent_kafka import Producer, Consumer

# Producer
p = Producer({'bootstrap.servers': 'localhost:9092'})
p.produce('orders.placed', key=str(order_id), value=order_json)
p.flush()

# Consumer
c = Consumer({'bootstrap.servers': 'localhost:9092', 'group.id': 'inventory-service'})
c.subscribe(['orders.placed'])
while True:
    msg = c.poll(1.0)
    if msg:
        process(msg.value())
        c.commit() # Acknowledge`,
        qa: [
            {
                q: 'Q: If I have 6 partitions and 8 consumers in one group, what happens?',
                a: `Only 6 consumers get work. 2 consumers sit idle.\n\nRule: A partition can only be consumed by ONE consumer per group. You can't have 8 consumers sharing 6 partitions — some will have nothing to do.\n\n✅ Optimization: Number of consumers = number of partitions for maximum parallelism.\n\nIf you need more throughput → increase partitions first, then add consumers to match.\n\nIMPORTANT: You can't reduce partitions once created. Plan ahead.`,
            },
            {
                q: 'Q: What is the difference between at-least-once vs exactly-once delivery?',
                a: `Simple scenario: Kafka sends you a "charge customer $100" event.\n\nAt-Most-Once: Commit offset BEFORE processing. If you crash mid-process → message is gone → customer NOT charged. Bad for payments.\n\nAt-Least-Once (default): Commit offset AFTER processing. If you crash → Kafka replays the message → "charge $100" runs AGAIN → double charge. Fix: make consumer idempotent (check if already processed).\n\nExactly-Once: Kafka transactions wrap produce + commit into one atomic operation. Much more complex. Use for financial transactions where duplicates cause real damage.\n\nFor most use cases: At-Least-Once + idempotent consumer is the pragmatic choice.`,
            },
            {
                q: 'Q: Consumer lag is growing. Walk me through how you would fix it.',
                a: `Consumer lag = new messages arriving faster than consumers are reading them. Think of it as a growing inbox.\n\nStep 1 — Measure: Check consumer_lag metric per partition. Is lag uniform or on specific partitions?\n\nStep 2 — Diagnose:\n• Slow processing in consumer? → profile the handler code\n• Not enough partitions? → can't add consumers beyond partition count\n• One heavy message blocking the rest? → DLQ (Dead Letter Queue) for bad messages\n\nStep 3 — Scale:\n• Add consumer instances (up to partition count)\n• If still not enough → increase partitions (requires planned migration)\n• Batch processing: increase max.poll.records from 100 to 500\n\nSTAR answer: "Payment service fell behind during flash sale. Added 4 consumer instances to match our 12 partitions, and enabled batch processing. Lag cleared in under 3 minutes."`,
                star: true,
            },
        ],
    },
    {
        id: 'ignite',
        icon: '🔥',
        color: '#ef4444',
        bg: 'rgba(239,68,68,0.15)',
        title: 'Apache Ignite',
        tagline: 'In-Memory Computing Grid',
        analogy: '🏎️ Ignite is like a racing pit crew with a shared memory board. Instead of each mechanic running to the car spec binder in the garage (disk), all specs are memorized on the shared board (RAM). Everyone reads from memory — 100x faster than walking to the garage.',
        simple: 'Ignite keeps your data IN memory across a cluster of machines. You get sub-millisecond reads, distributed SQL, and the ability to run your computation WHERE the data lives — eliminating expensive network transfers.',
        keyPoints: [
            'Data is partitioned: Node A owns keys 0-33%, Node B owns 34-66%, Node C owns 67-100%',
            'Replication: each partition has N backup copies — if Node A dies, Node B/C have the backup',
            'Affinity Colocation: run your Java code ON the same node as the data — zero network hops',
            'Near Cache: keep a local copy of HOT keys in each client — read without any network call',
            'Native Persistence: optional WAL (Write-Ahead Log) so data survives restarts',
        ],
        code: `// Java: SQL with Ignite
SqlFieldsQuery query = new SqlFieldsQuery("SELECT name FROM Product WHERE price > ?");
productCache.query(query.setArgs(100)).forEach(row -> System.out.println(row));

# Python Equivalent (pyignite):
from pyignite import Client
client = Client()
client.connect('127.0.0.1', 10800)
my_cache = client.get_or_create_cache('products')

result = client.sql("SELECT name FROM Product WHERE price > 100")
for row in result:
    print(row)`,
        qa: [
            {
                q: 'Q: What is affinity colocation and why does it make things faster?',
                a: `Simple analogy: Imagine you run a bakery. Each baker (node) is responsible for specific cake orders (data partitions). To frost a cake, would you:\n\nA) Bring the cake to a central frosting station (network transfer)\nB) Give each baker their own frosting supplies (affinity colocation)\n\nOption B is 10-100x faster with in-memory systems.\n\nWithout colocation: your Java code runs on Node 1, needs data from Node 3 → network call → 1-10ms latency.\n\nWith affinity colocation: Ignite routes your Java code to Node 3 where the data lives → reads local RAM → 0.01ms latency.\n\nHow to use: annotate your data class with @AffinityKeyMapped on the field that determines placement, then use ignite.compute().affinityRun() or affinityCall().`,
            },
            {
                q: 'Q: How is Ignite different from Redis?',
                a: `Redis: Simple key-value store. Very fast. Great for session caching, rate limiting, pub/sub, leaderboards. Limited query power.\n\nIgnite: Full distributed computing platform:\n• ANSI SQL queries across distributed memory (not just key lookups)\n• Collocated compute grid — run code WHERE data lives\n• ACID transactions across multiple keys/nodes\n• Can replace both your cache AND part of your database tier\n\nChoose Redis when: simple caching, pub/sub, sorted sets\nChoose Ignite when: complex SQL over cached data, collocated computation, ACID transactions needed\n\nIn this job's stack, Ignite handles real-time operational queries (current orders, live inventory) while Cassandra handles historical data storage.`,
            },
            {
                q: 'Q: How does Ignite handle a node failure?',
                a: `Step-by-step recovery:\n\n1. DETECTION: Ignite nodes send heartbeats every 2 seconds. If a node misses 3+ heartbeats → declared dead.\n\n2. PROMOTION: Each partition has "backup" copies on other nodes. Backup copies are immediately promoted to primary.\n\n3. REBALANCING: The cluster detects it's now under-replicated (lost a backup slot) → redistributes partitions to restore the configured backup count.\n\n4. DATA SAFETY:\n• With backups=1: one node can die → zero data loss\n• With backups=0: node dies → data on those partitions is lost (only safe with Native Persistence)\n\n5. NATIVE PERSISTENCE: If enabled, Ignite writes a WAL to disk → can fully recover a node that died and restarted.`,
            },
        ],
    },
    {
        id: 'spark',
        icon: '✨',
        color: '#8b5cf6',
        bg: 'rgba(139,92,246,0.15)',
        title: 'Apache Spark',
        tagline: 'Big Data Processing Engine',
        analogy: '📦 Spark is like a warehouse sorting system. You have pallets of boxes (data partitions) spread across many aisles (nodes). Instead of bringing all boxes to one counter, you send sorters to each aisle simultaneously — process in parallel, combine results at the end.',
        simple: 'Spark processes massive datasets (terabytes/petabytes) by splitting data into partitions and running operations in parallel across many machines. It\'s like MapReduce but 100x faster because it keeps data in memory between steps.',
        keyPoints: [
            'RDD is the old way; DataFrame is the modern way — use DataFrame/Dataset API (Catalyst optimizes it)',
            'Lazy evaluation: writing df.filter().groupBy() builds a plan but does NOTHING until .count() or .write()',
            'Narrow transformation (filter, map): no data shuffling. Wide transformation (groupBy, join): causes shuffle',
            'Shuffle = data moves across the network = expensive. Minimize it by filtering/projecting early',
            'Catalyst Optimizer: Spark rewrites your query plan before running it (like a smart SQL query planner)',
        ],
        code: `// Java: Spark DataFrame transformation
Dataset<Row> revenue = orders.filter(col("status").equalTo("COMPLETE"))
    .groupBy("category")
    .agg(sum("amount").as("total"));

# Python Equivalent (PySpark):
# PySpark syntax is almost identical but more clean
revenue = orders.filter(orders.status == "COMPLETE") \\
    .groupBy("category") \\
    .agg(sum("amount").alias("total"))`,
        qa: [
            {
                q: 'Q: What is the difference between a narrow and wide transformation? Why does it matter?',
                a: `Narrow (no shuffle): Each input partition maps to exactly ONE output partition. Data stays local to the node.\n• filter(), map(), select(), union()\n• FAST — no network transfer\n\nWide (shuffle required): Each output partition may need data from MULTIPLE input partitions. Data is physically moved across the network.\n• groupBy(), join(), distinct(), repartition()\n• SLOW — network I/O is the bottleneck\n\nWhy it matters for performance:\nSpark groups consecutive narrow transformations into one stage. Wide transformations create stage boundaries (shuffle).\n\nOptimization: always filter() and select() BEFORE join() or groupBy() to reduce the amount of data shuffled across the network.`,
            },
            {
                q: 'Q: What is data skew and how do you fix it?',
                a: `Problem: Imagine grouping orders by customer. If 1 customer (Amazon) placed 10 million orders and everyone else placed 10 — one partition has 10M rows, others have 10. One task runs for hours; others finish in seconds. The entire job waits.\n\nDetect: Spark UI → Stage → Task metrics → look for one task 100x slower than others.\n\nFix 1 — Salting:\n• Add random suffix to key: "amazon_1", "amazon_2", "amazon_3"\n• Process each salt bucket → union results\n• Distributes 10M rows across 3 partitions instead of 1\n\nFix 2 — Broadcast Join:\n• If one table is small (< 10GB) use broadcast\n• spark.sql.autoBroadcastJoinThreshold = 10GB\n• Small table is copied to every node → eliminates shuffle completely\n\nFix 3 — AQE (Spark 3+):\n• spark.sql.adaptive.skewJoin.enabled = true\n• Spark automatically detects and splits skewed partitions at runtime`,
            },
        ],
    },
    {
        id: 'postgres',
        icon: '🐘',
        color: '#0ea5e9',
        bg: 'rgba(14,165,233,0.15)',
        title: 'PostgreSQL',
        tagline: 'Relational Database (OLTP)',
        analogy: '📚 PostgreSQL is like a library with a card catalog (indexes). Without indexes = librarian reads every book title to find yours (sequential scan). With a B-Tree index = librarian goes straight to the right shelf (logarithmic lookup, O(log N)).',
        simple: 'PostgreSQL is your gold-standard relational database: ACID transactions, complex JOINs, strong consistency. Use it for any data where correctness matters more than scale (orders, users, inventory, payments).',
        keyPoints: [
            'ACID: Atomicity (all-or-nothing), Consistency (rules enforced), Isolation (concurrent txns don\'t see each other\'s dirty data), Durability (committed = safe)',
            'B-Tree index: default, great for equality and range queries (WHERE id = 5, WHERE created > today)',
            'GIN index: for JSONB columns and full-text search (WHERE metadata @> \'{"type":"web"}\')',
            'EXPLAIN ANALYZE: shows HOW PostgreSQL executes your query — look for Seq Scan on large tables',
            'MVCC: readers never block writers — old row versions exist until VACUUM cleans them up',
        ],
        code: `// Java (Spring Data JPA / JDBC)
@Query("SELECT * FROM orders WHERE status = :status")
List<Order> findByStatus(String status);

# Python Equivalent (psycopg2 / SQLAlchemy):
# raw SQL
cursor.execute("SELECT * FROM orders WHERE status = %s", (status,))
# SQLAlchemy ORM
orders = session.query(Order).filter(Order.status == status).all()`,
        qa: [
            {
                q: 'Q: Explain MVCC in simple terms.',
                a: `MVCC = Multi-Version Concurrency Control.\n\nThe problem it solves: Without MVCC, if User A is reading a row and User B wants to update it → one must wait (lock).\n\nMVCC solution: Instead of locking, PostgreSQL keeps multiple versions of each row.\n\nWhat happens when you UPDATE:\n• The old row version is NOT deleted immediately — it gets an "expired" marker (xmax)\n• A new row version is created with a "born at" timestamp (xmin)\n• Both versions exist at the same time\n\nWhat this means:\n• Reader sees the version that existed when their transaction started\n• Writer creates a new version without touching what the reader sees\n• READERS NEVER BLOCK WRITERS AND WRITERS NEVER BLOCK READERS\n\nCost: Dead row versions accumulate → VACUUM periodically cleans them up. This is why PostgreSQL has an autovacuum process.`,
            },
            {
                q: 'Q: When would you NOT use an index?',
                a: `1. Low-cardinality columns: A boolean column (true/false) — if 50% of rows are true, the index is useless. PostgreSQL will do a sequential scan instead.\n\n2. Small tables: If the table has < 10,000 rows, PostgreSQL may prefer scanning all rows (it's cached in memory anyway).\n\n3. High-write, low-read tables: Every INSERT/UPDATE/DELETE must update ALL indexes. A table with 10 indexes has 10x write overhead. A logging table should have minimal indexes.\n\n4. Function on indexed column: WHERE UPPER(email) = ... does NOT use an index on (email). You need a functional index: CREATE INDEX ON users(UPPER(email)).\n\n5. Very frequent bulk loads: DROP indexes before a large import, then recreate them — much faster than maintaining indexes during bulk insert.`,
            },
        ],
    },
    {
        id: 'cassandra',
        icon: '💾',
        color: '#a855f7',
        bg: 'rgba(168,85,247,0.15)',
        title: 'Apache Cassandra',
        tagline: 'Distributed NoSQL (High Availability)',
        analogy: '🏪 Cassandra is like a chain of 24/7 convenience stores. Each store (node) has copies of the most popular items. If one store burns down, others serve customers without interruption. But you can\'t ask "which store has the cheapest milk nationally?" — you can only ask store-specific questions (partition key lookups).',
        simple: 'Cassandra is built for massive write throughput and never going down. Unlike PostgreSQL, it has no single master — ALL nodes are equal. The cost: no JOINs, no arbitrary queries. You design your database schema around your queries.',
        keyPoints: [
            'Design schema PER QUERY, not per entity. One query = one table (denormalize!)',
            'Partition key: determines which node stores the data. MUST be in your WHERE clause',
            'Clustering columns: sort order within a partition. Allow range queries WITHIN a partition',
            'Tunable consistency: ONE=fastest/less safe, QUORUM=balanced, ALL=slowest/safest',
            'Write path: data written to commit log → memtable (RAM) → flushed to SSTable (disk) → compaction',
        ],
        code: `-- Cassandra CQL (NoSQL)
SELECT * FROM orders_by_user WHERE user_id = ? LIMIT 20;

# Python Equivalent (cassandra-driver):
from cassandra.cluster import Cluster
session = Cluster(['localhost']).connect('my_keyspace')

query = "SELECT * FROM orders_by_user WHERE user_id = %s LIMIT 20"
rows = session.execute(query, [user_id])
for row in rows:
    print(row.amount)`,
        qa: [
            {
                q: 'Q: Why no JOINs in Cassandra? How do you handle relationships?',
                a: `Why no JOINs: A JOIN needs data from multiple tables which may live on different nodes. Cassandra would need to:\n1. Scatter-gather data from N nodes\n2. Transfer potentially huge amounts of data across the network\n3. Sort and merge on the coordinator node\n\nThis defeats Cassandra's purpose — linear scalability and predictable single-partition latency.\n\nHow to handle relationships:\nDuplicate the data! Create one table per access pattern:\n• orders_by_user: find orders FOR a user\n• orders_by_status: find orders WITH a specific status\n• orders_by_region: find orders IN a region\n\nThe same order record exists in all 3 tables. Storage is cheap. Network round-trips across 1M nodes are not.\n\nAt write time: write to all relevant tables (in the application layer or via Cassandra batch).`,
            },
            {
                q: 'Q: What is a "hot partition" and how do you avoid it?',
                a: `Hot partition: one partition key is so popular that one Cassandra node gets 90% of the traffic while others sit idle.\n\nExample of BAD partition key: date ("2024-01-15")\n→ ALL of today's writes go to ONE node. Tomorrow = different node. But during the day, extreme imbalance.\n\nExample of BAD partition key: country ("US")\n→ US has 10x more users than others. US partition node is overwhelmed.\n\nFixes:\n1. Composite key: (country, bucket) where bucket = user_id % 10\n   → US traffic splits across 10 partitions on different nodes\n\n2. Time bucketing: (user_id, year_month) instead of just (user_id)\n   → Prevents unbounded partition growth (Cassandra limit: ~100MB per partition)\n\n3. Random prefix: add random(0,N) prefix to key at write time, read from all N partitions and merge`,
            },
        ],
    },
    {
        id: 'yellowbrick',
        icon: '🏗️',
        color: '#f59e0b',
        bg: 'rgba(245,158,11,0.12)',
        title: 'Yellowbrick',
        tagline: 'Analytical Data Warehouse (OLAP)',
        analogy: '📊 Yellowbrick is like a fast accountant with a massive spreadsheet. Cassandra is your cash register (fast transactions). Yellowbrick is the accountant who takes ALL your receipts and answers "What were total sales by region last quarter?" in seconds.',
        simple: 'Yellowbrick is purpose-built for analytics — complex SQL queries over huge datasets. It\'s NOT for individual record lookups (use PostgreSQL/Cassandra for that). It\'s for business intelligence, reporting, and feeding ML models.',
        keyPoints: [
            'OLAP vs OLTP: Yellowbrick = analytical (complex reads, many rows). PostgreSQL = transactional (CRUD on individual rows)',
            'Columnar storage: stores all values of one column together. Analytics reads only the columns it needs — 10-100x less I/O',
            'MPP (Massively Parallel Processing): query splits across all nodes simultaneously, results merged',
            'Typical data flow: Kafka → Spark ETL → Yellowbrick → BI tools (Tableau, Looker)',
            'vs Snowflake: Yellowbrick runs on YOUR infrastructure. Better for regulated industries and predictable concurrent workloads',
        ],
        code: `-- Yellowbrick uses ANSI SQL — looks like PostgreSQL

-- Example: Rolling 7-day revenue by product category
-- This type of query would be painfully slow in Cassandra or PostgreSQL at scale
SELECT
  category,
  event_date,
  SUM(revenue) AS day_revenue,
  SUM(SUM(revenue)) OVER (
    PARTITION BY category
    ORDER BY event_date
    ROWS BETWEEN 6 PRECEDING AND CURRENT ROW
  ) AS rolling_7d_revenue,
  AVG(SUM(revenue)) OVER (
    PARTITION BY category
    ORDER BY event_date
    ROWS BETWEEN 29 PRECEDING AND CURRENT ROW
  ) AS rolling_30d_avg
FROM daily_sales
WHERE event_date >= CURRENT_DATE - INTERVAL '90 days'
GROUP BY category, event_date
ORDER BY category, event_date DESC;

-- Yellowbrick distributes this across all nodes:
-- Node 1 handles category=Electronics for date range X
-- Node 2 handles category=Clothing for date range X
-- Coordinator merges and returns final result`,
        qa: [
            {
                q: 'Q: When would you choose Yellowbrick vs PostgreSQL vs Cassandra for a feature?',
                a: `Use this simple decision tree:\n\nIs it a transaction (individual record read/write)?\n→ YES: Does it need ACID? → YES = PostgreSQL. NO or massive scale + high write throughput = Cassandra\n\nIs it an analytical query (aggregations, complex JOINs, trends over time)?\n→ YES: Is data > 100GB or queries complex? → YES = Yellowbrick. Small scale = PostgreSQL can handle it\n\nReal example:\n• Store an order → PostgreSQL (ACID, relational)\n• Store 10M IoT sensor readings/day → Cassandra (write throughput)\n• Calculate weekly revenue by region for 3 years of data → Yellowbrick (OLAP)\n\nIn this job's tech stack all three coexist:\n• Cassandra = operational data store (recent activity)\n• PostgreSQL = master reference data (users, products)\n• Yellowbrick = analytics and reporting layer`,
            },
            {
                q: 'Q: Explain columnar storage in simple terms.',
                a: `Row storage (PostgreSQL): Each row stored together on disk.\n[Alice, 30, Marketing, $95,000]\n[Bob, 25, Engineering, $120,000]\n[Carol, 35, Marketing, $105,000]\n\nTo calculate average salary: PostgreSQL reads ALL columns of ALL rows even though you only need salary.\n\nColumnar storage (Yellowbrick): Each column stored together.\nNames: [Alice, Bob, Carol]\nAges: [30, 25, 35]\nSalaries: [$95K, $120K, $105K]\n\nTo calculate average salary: Yellowbrick reads ONLY the Salary column.\n→ For 1 billion employees, you read 8 bytes × 1B instead of 200 bytes × 1B\n→ 25x less I/O instantly\n\nBonus: Columnar data compresses 10x better (similar values are adjacent → compression algorithms love this).`,
            },
        ],
    },
    {
        id: 'distributed',
        icon: '🌐',
        color: '#06b6d4',
        bg: 'rgba(6,182,212,0.15)',
        title: 'Distributed Systems',
        tagline: 'Fault Tolerance & Scalability Patterns',
        analogy: '🤝 Distributed systems are like running a company with offices in 5 cities. Communication has a delay (network latency). An office might burn down (node failure). Employees can disagree on facts (consistency problems). Your job: design so the company keeps operating despite these realities.',
        simple: 'When your app runs on many servers, you get new classes of problems: network failures, conflicting data, cascading failures. Distributed systems patterns are battle-tested solutions to these problems.',
        keyPoints: [
            'API Gateway: Central entry point (Vert.x) for auth, rate limiting, and routing.',
            'Service Discovery: Dynamically finding service locations (Eureka/Consul) instead of hardcoding IPs.',
            'Distributed Config: Centralized management of application properties (Spring Cloud Config).',
            'Observability: The "Three Pillars" — Logs (ELK), Metrics (Prometheus/Grafana), and Tracing (Jaeger/Zipkin).',
            'CAP Theorem: During a network partition, you must choose Consistency OR Availability.',
            'Resiliency: Bulkheads separate thread pools so one slow service doesn’t kill the whole app.',
        ],
        code: `// Java: Resiliency (Resilience4j)
@CircuitBreaker(name = "inventory", fallbackMethod = "fallback")
public String callInventory() { return restTemplate.get(...); }

# Python Equivalent (Tenacity/CircuitBreaker):
from circuitbreaker import circuit

@circuit(failure_threshold=5, recovery_timeout=30)
def call_inventory():
    return requests.get(...)

def fallback():
    return "Cached Stock"`,
        qa: [
            {
                q: 'Q: What is the difference between client-side and server-side load balancing?',
                a: `**Server-side (Nginx/F5):** Client hits a single IP/DNS; the balancer picks a backend. Simple for clients but is a potential bottleneck.\n\n**Client-side (Spring Cloud LoadBalancer):** The client gets a list of all healthy IPs from Service Discovery and picks one itself. Reduces hardware costs and eliminates a central bottleneck.`,
            },
            {
                q: 'Q: How do you handle "Distributed Tracing" and why is it needed?',
                a: `In monoliths, one log file shows everything. In microservices, a single request might hit 10 services. Without tracing, debugging a slow request is impossible.\n\n**Solution:** Use a **Trace ID** that is passed in headers (X-Trace-Id) across all service calls. OpenTelemetry/Jaeger collects these spans to visualize the entire request timeline.`,
            },
            {
                q: 'Q: A payment service is calling inventory service. Inventory is slow. How do you fix it without taking down payments?',
                a: `This is a cascading failure scenario. Without protection:\nInventory is slow → payment threads wait → payment thread pool exhausted → payment service crashes too.\n\nFix with the "Defense in Depth" pattern:\n\n1. TIMEOUT: payment service waits max 500ms for inventory. If no response → proceed with "best-effort" inventory check.\n\n2. CIRCUIT BREAKER: after 5 timeouts in 10 seconds → circuit opens → immediately return cached stock levels for 30 seconds → try again (half-open state).\n\n3. BULKHEAD: inventory calls use a SEPARATE thread pool (10 threads). Even if all 10 are stuck, payment's 10 main threads continue serving other requests.\n\n4. FALLBACK: when circuit is open, show "In Stock" (optimistic) or "Check availability at checkout" rather than error.\n\nReal answer: "We used Resilience4j @CircuitBreaker + @TimeLimiter annotations on the inventory client. Customer experience was degraded (optimistic stock display) but payment service stayed up during a 4-hour inventory service incident."`,
                star: true,
            },
            {
                q: 'Q: Explain the difference between 2-Phase Commit and Saga pattern.',
                a: `2-Phase Commit (2PC):\nCoordinator asks all participants to PREPARE → all vote YES → coordinator sends COMMIT.\n\nProblems:\n• If coordinator crashes after PREPARE but before COMMIT → participants are LOCKED forever\n• Every participant must hold locks during the whole process → low throughput\n• Doesn't work across different database technologies\n\nSaga Pattern:\nEach step commits locally and publishes an event. No global lock needed.\n\nChoreography Saga:\nStep 1 commits → emits event → Step 2 listens → commits → emits event → ...\nPro: decoupled. Con: hard to track overall state.\n\nOrchestration Saga:\nA Saga Orchestrator (e.g., Temporal) tells each service what to do next.\nPro: centralized visibility. Con: orchestrator is stateful.\n\nWhen rollback needed: compensating transactions run in reverse order.\nExample: "charge payment" → compensation is "refund payment"\n\nUse Saga when: services have different databases, 2PC overhead is too high, or you need cross-service ACID-like behavior.`,
            },
        ],
    },
    {
        id: 'agile',
        icon: '🔄',
        color: '#10b981',
        bg: 'rgba(16,185,129,0.15)',
        title: 'Agile & Professional Skills',
        tagline: 'Working Practices & Behavioral Interview',
        analogy: '🚢 Agile is like steering a ship with a small rudder — make small course corrections frequently rather than one huge turn at the end. Traditional waterfall = plan the full route in January, discover you\'re off course in December.',
        simple: 'Agile is about delivering working software in small increments, learning from feedback, and adapting. Interviewers want to know: can you work in a team, handle ambiguity, communicate clearly, and learn fast?',
        keyPoints: [
            'Sprint = 2-week timebox. Ship working software every sprint, not just at the end',
            'Definition of Done: story is complete ONLY when code-reviewed + unit tested + deployed to staging',
            'Communicate blockers in standup — never hold a blocker until end of sprint',
            'TDD: write failing test FIRST, then minimum code to pass it, then refactor',
            'When requirements are vague: restate → ask clarifying questions → prototype the uncertain part → review',
        ],
        code: `// Java: TDD failing test
@Test
void shouldRejectNegative() {
  assertThrows(Exception.class, () -> service.create(-1));
}

# Python Equivalent (pytest):
def test_reject_negative():
    with pytest.raises(Exception):
        service.create(-1)`,
        qa: [
            {
                q: 'Q: Tell me about a time requirements changed mid-sprint. What did you do?',
                a: `STAR format:\n\nSituation: 3 days into a 2-week sprint, business stakeholder said they needed a "priority queue" feature for VIP customers added to the current payment sprint.\n\nTask: My role as senior dev was to assess impact and recommend a path forward.\n\nAction:\n1. Estimated the new feature: 5 story points (about 2.5 days of work)\n2. Identified a 5-point story in the sprint that could defer: a UI polish task\n3. Presented stakeholder with the swap: "We can add priority queuing IF we defer the UI polish to next sprint. Otherwise it's next sprint."\n4. Stakeholder agreed to the swap — protecting team's overall sprint capacity\n5. Documented the reason in Jira\n\nResult: Priority queuing shipped in the current sprint. UI polish shipped next sprint. No overtime. Team velocity stayed consistent.\n\nKey principle: Never silently absorb scope creep. Always make the trade-off explicit.`,
                star: true,
            },
            {
                q: 'Q: How do you explain a complex technical decision to a non-technical stakeholder?',
                a: `My ABCD framework:\n\nA — Analogy first: "Think of our database like a filing cabinet. Right now it's one giant cabinet in one room. We want to add 4 more cabinets, spread across 4 rooms (sharding)."\n\nB — Business impact: "This means we can handle 4x more customers without the system slowing down."\n\nC — Cost (tradeoff): "The downside: if you need a document that spans cabinets, the clerk has to visit multiple rooms — some queries will be slightly slower."\n\nD — Decision: "We recommend doing this now because in 6 months, the single cabinet will be full and customers will see slowdowns."\n\nAvoid: jargon (sharding, partitioning, horizontal scaling)\nUse: analogies, numbers, business impact, clear recommendation\n\nSTAR story: "I was asked why fixing a memory leak would take 3 days. I showed the stakeholder a video of Chrome tabs slowing down a computer. 'Our server has the same problem but for user sessions.' They approved the time immediately."`,
                star: true,
            },
        ],
    },
    {
        id: 'oop',
        icon: '🧱',
        color: '#06b6d4',
        bg: 'rgba(6,182,212,0.15)',
        title: 'Object Oriented Programming',
        tagline: 'The 4 Pillars + SOLID Principles',
        analogy: '🏠 Think of OOP like building a house. The Blueprint (Class) defines the structure. The House itself (Object/Instance) is what you actually live in. Encapsulation = the walls hide the wiring. Inheritance = a Villa extends a House (gets rooms, adds a pool). Polymorphism = every building has a "open door" method but each opens differently. Abstraction = you use a light switch without knowing the electrical circuit.',
        simple: 'OOP is a way of organizing code by grouping related data (fields) and behavior (methods) into objects. Instead of writing procedures that act on data, you create self-contained objects that know their own state and what they can do. The 4 pillars — Encapsulation, Inheritance, Polymorphism, Abstraction — give you tools to write code that is reusable, maintainable, and scalable.',
        keyPoints: [
            'Encapsulation: Hide internal state with private fields; expose only through public methods. Prevents invalid state.',
            'Inheritance: A subclass (Light) extends a superclass (SmartDevice) and inherits its fields and methods. "is-a" relationship.',
            'Polymorphism: One interface, many implementations. Method overriding (runtime) + method overloading (compile time).',
            'Abstraction: Hide complex implementation details behind a simple interface using Abstract classes and Interfaces.',
            'Composition over Inheritance: Prefer "has-a" relationship for better flexibility and less coupling.',
            'SOLID Principles: Five design principles for making software more understandable, flexible, and maintainable.',
        ],
        code: `// Java: Smart Home OOP Demo
abstract class SmartDevice {
    private String name; // Encapsulation
    protected boolean isOn;

    public SmartDevice(String name) { this.name = name; }
    public String getName() { return name; }
    public abstract void performAction(); // Abstraction
}

class Light extends SmartDevice { // Inheritance
    @Override
    public void performAction() { // Polymorphism
        isOn = true;
        System.out.println(getName() + " is shining!");
    }
}

# Python Equivalent:
from abc import ABC, abstractmethod

class SmartDevice(ABC):
    def __init__(self, name):
        self._name = name # Encapsulation
        self._is_on = False

    @abstractmethod
    def perform_action(self): # Abstraction
        pass

class Light(SmartDevice): # Inheritance
    def perform_action(self): # Polymorphism
        self._is_on = True
        print(f"{self._name} is shining!")`,
        qa: [
            {
                q: 'Q: What is the difference between an Interface and an Abstract Class in Java?',
                a: `Both enforce a contract, but they serve different purposes.

**Abstract Class:**
• Can have state (instance variables)
• Can have constructors
• Can have concrete methods (with implementation)
• Subclass can only extend ONE abstract class
• Use when subclasses share common state and behavior (template method pattern)

**Interface:**
• Fields are implicitly public static final (constants only)
• No constructors
• All methods are implicitly abstract (before Java 8). Java 8+ adds default and static methods
• A class can implement MULTIPLE interfaces
• Use when you want to define a pure contract that unrelated classes can fulfill

**When to choose which:**
Interface: "Can this behavior apply to wildly different objects?" (Comparable, Serializable, Runnable)
Abstract Class: "Do these objects share real state/behavior AND are clearly related?" (Payment → CreditCard, Crypto)

**Trick question (Liskov):** If Square extends Rectangle, calling setWidth(5) also sets height to 5, breaking the Rectangle contract. Subclass must be substitutable for the parent. This is why inheritance must model a true "is-a" relationship.`,
            },
            {
                q: 'Q: Explain polymorphism with a real-world example and how Java implements it.',
                a: `Polymorphism = "one interface, many forms."

**Two types in Java:**

1. Runtime Polymorphism (method overriding, most important):
   Payment p = new CreditCardPayment();
   p.process(100); // calls CreditCardPayment.process() at runtime
   // JVM looks at the actual object type, not the reference type → "dynamic dispatch"

2. Compile-time Polymorphism (method overloading):
   void log(String msg) { ... }
   void log(String msg, int level) { ... }  // same name, different parameters

**Real example from payment systems:**
List<Payment> payments = List.of(new CreditCardPayment(), new CryptoPayment(), new BankTransfer());
payments.forEach(p -> p.process(99.99)); // each behaves differently, but code is uniform

**Why it matters in interviews:** Polymorphism is the foundation of the Open/Closed principle (SOLID). When you add a new payment type, you don't touch existing code — you just add a new subclass. This is how large systems stay maintainable.`,
            },
            {
                q: 'Q: What is encapsulation and why does it matter beyond just "making fields private"?',
                a: `Encapsulation is about protecting object invariants — ensuring an object can never be in an invalid state.

**Surface level answer (not enough):** "Make fields private, expose via getters/setters."

**Deeper answer senior engineers give:**
Encapsulation means the object controls all mutations to its own state.

BAD — violates encapsulation:
public List<Order> orders = new ArrayList<>(); // caller can .clear() or add invalid orders

GOOD:
private List<Order> orders = new ArrayList<>();
public void addOrder(Order o) {
    if (o == null) throw new IllegalArgumentException(...);
    // apply business rules: max 10 orders per customer, etc.
    orders.add(o);
}
public List<Order> getOrders() {
    return Collections.unmodifiableList(orders); // prevent external mutation
}

**The real benefit:** When a bug causes an Order to enter invalid state, there's only ONE place to look — the addOrder() method. Without encapsulation, any part of the codebase could corrupt the list.

**Interview story format:** "In a payment system I worked on, we exposed account balance as a public field. A race condition caused double charges. We fixed it by encapsulating balance updates behind a synchronized debit() method that checked available funds atomically."`,
                star: true,
            },
            {
                q: 'Q: Explain the SOLID principles. Which one do you find hardest to apply and why?',
                a: `**S — Single Responsibility:** Each class has one reason to change.
Bad: UserService handles authentication, profile updates, AND sends emails.
Good: AuthService, ProfileService, EmailNotificationService.

**O — Open/Closed:** Open for extension, closed for modification.
Bad: Giant if-else in processPayment() for every payment type.
Good: PaymentStrategy interface + add new implementations without touching existing code.

**L — Liskov Substitution:** Subtypes must work everywhere the parent works.
Classic violation: Square extends Rectangle — setWidth(5) breaks height.

**I — Interface Segregation:** Don't force clients to implement methods they don't use.
Bad: IWorker with work(), eat(), sleep() — robots don't eat.
Good: IWorkable, IFeedable as separate interfaces.

**D — Dependency Inversion:** Depend on abstractions, not concrete classes.
Bad: new MySQLDatabase() inside OrderService.
Good: OrderService(DatabaseInterface db) — inject at construction time.

**Hardest to apply: Liskov Substitution** (most nuanced).
You can violate it subtly just by overriding a method and adding a new precondition the parent didn't have. The rule is: subclass can only WEAKEN preconditions and STRENGTHEN postconditions relative to the parent.`,
            },
            {
                q: 'Q: Composition vs Inheritance — when would you choose composition?',
                a: `**General rule: Favor composition over inheritance.**

**Inheritance problems:**
• Tight coupling — changing the parent breaks all subclasses
• Fragile base class problem: Animal gains a "breathe()" method → breaks FishRobot subclass
• Java is single-inheritance — you can only extend one class

**When inheritance is correct:** A true "is-a" relationship that won't change.
Dog is-a Animal. CreditCardPayment is-a Payment. Good.

**When composition is better:**
// BAD — inheritance just to reuse logging behavior:
public class OrderService extends Logger { ... } // OrderService is-NOT-A Logger

// GOOD — compose instead:
public class OrderService {
    private final Logger logger;          // HAS-A Logger
    private final EmailService emailer;   // HAS-A EmailService
    public OrderService(Logger l, EmailService e) { ... }
}

**Benefits of composition:**
• Swap implementations at runtime (inject different Logger, MockEmailer in tests)
• Mix-and-match behaviors (Logger + Emailer + Auditor without inheritance chains)
• No fragile base class problem

**Real answer in interviews:** "Before adding inheritance, I ask: is this truly an 'is-a' relationship that will never change? If I'm unsure, I start with composition. You can always refactor to inheritance later; refactoring away from it is much harder."`,
                star: true,
            },
        ],
    },
    {
        id: 'collections',
        icon: '📚',
        color: '#fbbf24',
        bg: 'rgba(251,191,36,0.15)',
        title: 'Java Collections',
        tagline: 'The Foundation of Data Handling',
        analogy: '🗄️ Think of the Collections Framework as a warehouse with different storage units. A List is a simple shelf (ordered, duplicates ok). A Set is a unique security locker (no duplicates). A Map is a coat check (give a ticket, get your coat). Choosing the right unit makes retrieving data 100x faster.',
        simple: 'The Collections Framework is a set of classes and interfaces that implement commonly used data structures like lists, sets, and maps. It provides a standard way to store and manipulate groups of objects, making your code more reusable and efficient.',
        keyPoints: [
            'Hierarchy: Iterable → Collection → (List, Set, Queue). Map is a separate hierarchy.',
            'ArrayList vs LinkedList: ArrayList = O(1) read, O(n) insert; LinkedList = O(n) read, O(1) insert at ends.',
            'HashMap Internals: Uses an array of buckets. Collision handling via linked lists/red-black trees.',
            'HashSet: Built on top of HashMap (key = element, value = dummy object).',
            'ConcurrentHashMap: Thread-safe without locking the whole map (uses bucket-level locking).',
        ],
        code: `// Java: Map Lookup
Map<String, String> map = new HashMap<>();
map.put("key", "value");
String val = map.get("key");

// Java: List Filter (Streams)
List<String> filtered = list.stream()
    .filter(s -> s.startsWith("A"))
    .collect(Collectors.toList());

# Python Equivalent:
# Dictionary Lookup
my_dict = {"key": "value"}
val = my_dict["key"]

# List Comprehension
filtered = [s for s in my_list if s.startswith("A")]`,
        qa: [
            {
                q: 'Q: How does HashMap work internally in Java?',
                a: `It uses a technique called Hashing.\n\n1. BUCKETS: HashMap has an internal array of buckets.\n2. HASH CODE: When you put(key, value), Java calculates key.hashCode().\n3. INDEX: (n - 1) & hash gives the bucket index.\n4. COLLISION: If two keys land in the same bucket, they are stored in a LinkedList (or a Red-Black Tree if > 8 items).\n5. RETRIEVAL: Java finds the bucket, then uses key.equals() to find the exact match.\n\nKey to performance: a good hashCode() that distributes keys evenly across buckets.`,
            },
            {
                q: 'Q: Why is ArrayList generally preferred over LinkedList even for insertions?',
                a: `While LinkedList has O(1) insertion *at the pointer*, finding the location is O(n). Additionally, ArrayList is much more cache-friendly because elements are stored in contiguous memory.\n\nModern CPUs are extremely fast at moving blocks of memory. In many real-world tests, ArrayList beats LinkedList even for middle-insertions because of CPU cache hits and the high overhead of creating a new Node object for every element in LinkedList.`,
            },
            {
                q: 'Q: Difference between fail-fast and fail-safe iterators?',
                a: `Fail-fast (e.g., ArrayList, HashMap): Throws **ConcurrentModificationException** if the collection is modified while iterating. It uses a 'modCount' field to track structural changes.\n\nFail-safe (e.g., CopyOnWriteArrayList, ConcurrentHashMap): Operates on a clone or uses a specialized data structure that allows concurrent modification. It doesn't throw the exception even if the source is changed during iteration.`,
            },
            {
                q: 'Q: Stack vs Queue vs Deque — which should I use for LIFO/FIFO?',
                a: `**FIFO (First-In-First-Out):** Use **Queue** interface (LinkedList or ArrayDeque).\n**LIFO (Last-In-First-Out):** Use **Deque** interface (ArrayDeque) instead of the old 'Stack' class (which is synchronized and slow).\n\n**Java:**\n\`Queue<String> q = new LinkedList<>();\` (FIFO)\n\`Deque<Integer> s = new ArrayDeque<>(); s.push(1);\` (LIFO)\n\n**Python:**\n\`from collections import deque\`\n\`q = deque(); q.append(1); q.popleft()\` (FIFO)\n\`s = []; s.append(1); s.pop()\` (LIFO)`,
            },
            {
                q: 'Q: When is Binary Search better than Linear Search?',
                a: `Always when the data is **sorted**. Linear search is O(N), Binary Search is O(log N).\n\nFinding 1 item in 1 million:\n• Linear: ~1,000,000 checks\n• Binary: ~20 checks (2^20 ≈ 1,000,000)`,
            },
        ],
    },
    {
        id: 'concurrency',
        icon: '🧵',
        color: '#6366f1',
        bg: 'rgba(99,102,241,0.15)',
        title: 'Java Concurrency',
        tagline: 'Multi-threaded Excellence',
        analogy: '👨‍🍳 Multithreading is like a kitchen with 4 chefs. If they all try to use the same knife at once, someone gets hurt (Race Condition). If Chef A waits for Chef B\'s spoon, and Chef B waits for Chef A\'s knife, no work gets done (Deadlock). Synchronization is the kitchen manager ensuring everyone plays safe.',
        simple: 'Concurrency in Java allows your application to execute multiple tasks at the same time. This is critical for high-performance servers (like this one!) where you want to handle many user requests simultaneously without making them wait for each other.',
        keyPoints: [
            'Thread vs Runnable: Runnable is the task; Thread is the worker. Always prefer ExecutorService over manual new Thread().',
            'Race Condition: Two threads reading/writing the same variable simultaneously → unpredictable results.',
            'Synchronized: A lock that ensures only one thread enters a code block at a time.',
            'Volatile: Ensures a variable is read from and written to main memory, not CPU cache (ensures visibility).',
            'Thread Pool: Reuses existing threads instead of creating new ones (expensive).',
        ],
        code: `// Java: CompletableFuture (Async)
CompletableFuture.supplyAsync(() -> fetchUser())
    .thenAccept(user -> System.out.println(user));

# Python Equivalent (asyncio):
import asyncio
async def main():
    user = await fetch_user()
    print(user)
asyncio.run(main())`,
        qa: [
            {
                q: 'Q: What is a Race Condition? Give an example and how to fix it.',
                a: `A Race Condition happens when two threads try to update the same shared variable at the same time.\n\nExample: counter = counter + 1\nThread A reads 10. Thread B reads 10. Thread A writes 11. Thread B writes 11.\nExpected result was 12, but we got 11.\n\nHow to fix:\n1. **synchronized** keyword on the method/block.\n2. **ReentrantLock** for more control.\n3. **AtomicInteger** / **AtomicLong** for high-performance lock-free counting.\n4. **ThreadLocal** if threads don't actually need to share the data.`,
            },
            {
                q: 'Q: What is the difference between wait() and sleep()?',
                a: `• **wait()**: Releases the lock (monitor) so other threads can enter the synchronized block. Used for thread inter-communication (must be in synchronized). It is a method of Object.\n• **sleep()**: Does NOT release the lock. It just pauses execution. It is a static method of Thread.\n\nAnalogy: wait() is like a chef leaving the stove to let another chef cook while they wait for ingredients. sleep() is like the chef taking a nap while still standing in front of the stove — nobody else can use it.`,
            },
            {
                q: 'Q: Why is volatile needed if we have synchronized?',
                a: `synchronized handles TWO things: Atomicity + Visibility.\nvolatile ONLY handles **Visibility**.\n\nWhen a variable is volatile, changes made by one thread are immediately visible to others (no CPU cache lag). It does NOT prevent race conditions for operations like i++. Use volatile for flags (e.g., boolean running) where you only care that everyone sees the latest value.`,
            },
        ],
    },
    {
        id: 'jvm',
        icon: '⚙️',
        color: '#6366f1',
        bg: 'rgba(99,102,241,0.15)',
        title: 'JVM Internals',
        tagline: 'Under the Hood',
        analogy: '🏭 The JVM is like a factory floor. The Class Loader brings in raw materials (class files). The Runtime Area is the floor where materials are laid out (Stack/Heap). The Execution Engine is the machinery that processes them. The Garbage Collector is the cleaning crew that removes scrap to keep the floor clear.',
        simple: 'The Java Virtual Machine (JVM) is the engine that runs Java byte code. Understanding how it manages memory (Heap vs Stack) and how it handles garbage collection is essential for writing memory-efficient, high-performance applications.',
        keyPoints: [
            'JVM Architecture: Class Loader, Runtime Data Areas (Heap, Stack, PC, Native), Execution Engine (JIT).',
            'Heap vs Stack: Heap stores objects (shared); Stack stores local variables and method calls (per thread).',
            'Garbage Collection (GC): Automated process of deleting unused objects to free memory.',
            'GC Algorithms: Parallel (throughput), G1 (low pause), ZGC (ultra-low pause for huge heaps).',
            'Memory Leak: Objects no longer used but still referenced, so GC cannot reclaim them.',
        ],
        code: `// 1. JVM Memory Tuning
// -Xms512m (Start heap)
// -Xmx2g   (Max heap)
// -XX:+UseG1GC (Enable G1 Garbage Collector)

// 2. Monitoring Memory
Runtime runtime = Runtime.getRuntime();
long usedMemory = runtime.totalMemory() - runtime.freeMemory();
System.out.println("Used: " + usedMemory / (1024 * 1024) + " MB");

// 3. Potential Memory Leak
public class LeakExample {
    private static List<Object> cache = new ArrayList<>(); // Static never dies!
    public void addToCache(Object obj) {
        cache.add(obj); // Keep adding without clearing = Leak
    }
}`,
        qa: [
            {
                q: 'Q: What is the difference between Stack and Heap memory?',
                a: `• **Stack**: Stores method-specific data (local variables, parameters) and the call stack. Each thread has its own stack. Access is very fast (O(1)). Memory is reclaimed automatically when method returns.\n• **Heap**: Stores all objects ('new Object()'). Shared among all threads. GC manages this memory. Much larger than stack, but access is slower.\n\nClassic mistake: "Objects are on heap, primitives on stack."\nReal rule: Local variables (primitives OR reference address) are on stack. ALL objects (including member primitives) are on heap.`,
            },
            {
                q: 'Q: How does the G1 Garbage Collector work?',
                a: `G1 (Garbage First) divides the heap into many small regions (1MB to 32MB each).\n\n1. It identifies which regions are mostly garbage.\n2. It collects those "Garbage First" to get the most free space with the least work.\n3. It uses a "Pause Prediction Model" to meet a user-defined pause time goal (e.g., "don't stop the app for more than 200ms").\n\nIt is much more efficient for large heaps (> 4GB) than the older Parallel or CMS collectors.`,
            },
            {
                q: 'Q: What causes a Memory Leak in a managed-memory language like Java?',
                a: `Even with GC, you can have leaks. A leak in Java is when an object is **logically dead** but **physically alive** (still referenced).\n\nTop causes:\n1. **Static Collections**: A static List that keeps growing and is never cleared.\n2. **Unclosed Resources**: Not closing DB connections or File streams.\n3. **Interned Strings**: Massive use of String.intern() on unique strings.\n4. **Incorrect equals/hashCode**: Using a Custom object as a Map key without implementing equals/hashCode correctly → you keep adding copies because Map can't find the original to replace it.`,
            },
        ],
    },
    {
        id: 'java8',
        icon: '🚀',
        color: '#6366f1',
        bg: 'rgba(99,102,241,0.15)',
        title: 'Java 8+ Modern Features',
        tagline: 'Functional Java',
        analogy: '⚡ Java 8 was like giving a car an electric engine (Streams/Lambdas). It change how you "drive" (code). Instead of telling the computer HOW to loop (imperative), you tell it WHAT you want (declarative). "Give me all red cars" vs "Check car 1, if red keep, check car 2..."',
        simple: 'Java 8 introduced functional programming concepts to Java. Highlights include Lambda expressions, the Streams API for data processing, and the Optional class to handle nulls safely. It made Java code significantly more concise and readable.',
        keyPoints: [
            'Lambda Expressions: Anonymous functions; (param) -> { body }.',
            'Functional Interfaces: Interface with exactly one abstract method (e.g., Predicate, Function).',
            'Streams API: Declarative data processing (filter, map, reduce, collect).',
            'Optional: A container object used to contain not-null objects to avoid NullPointerExceptions.',
            'Default Methods: Interfaces can now have method implementations.',
        ],
        code: `// Java: Streams & Optional
list.stream().filter(s -> s.startsWith("a")).count();
Optional<String> name = Optional.ofNullable(val).orElse("Guest");

# Python Equivalent:
# Filtering with list comprehension
count = len([s for s in my_list if s.startswith("a")])
# Simple OR for defaults
name = val or "Guest"`,
        qa: [
            {
                q: 'Q: What is the difference between Intermediate and Terminal stream operations?',
                a: `Streams are **lazy**.\n\n• **Intermediate** (filter, map, sorted): Returns a new stream. They DON'T execute until a terminal operation is called. They allow for "pipeline" optimization.\n• **Terminal** (forEach, collect, count, reduce): Triggers the execution of the pipeline and returns a result or void.\n\nAnalogy: Intermediate is like a chef deciding the recipe. Terminal is the chef actually starting to cook.`,
            },
            {
                q: 'Q: Why should we use Optional instead of null checks?',
                a: `Optional is a **type-level hint**. It tells the developer: "The result might be empty; you MUST handle it."\n\nIt forces you to think about the empty case at compile time, leading to fewer Runtime NullPointerExceptions. It also enables a cleaner, more fluent API style using .map() and .orElse().\n\nCrucial: Don't use Optional for method parameters or as fields in Serializable classes; use it primarily as a return type for "finder" methods.`,
            },
            {
                q: 'Q: What are Default Methods and why were they added?',
                a: `Default methods allow you to add new methods to an interface WITHOUT breaking existing implementations. This was essential for adding the .stream() method to the Collection interface in Java 8.\n\nIf you implement Two interfaces with the same default method name, Java will force you to override it to resolve the "Diamond Problem" conflict.`,
            },
        ],
    },
    {
        id: 'management',
        icon: '🤝',
        color: '#f43f5e',
        bg: 'rgba(244,63,94,0.15)',
        title: 'People Management',
        tagline: 'Leadership & Team Building',
        analogy: '⚽ Think of a team like a professional soccer team. Everyone has a specialized role (striker, goalie, mid), but we win or lose together. During the tournament (project), roles are fixed, but we are constantly learning and evolving so we can adapt when the game changes.',
        simple: 'My leadership style is grounded in being **Customer Obsessed**, **Owners at Heart**, and **Curious and Ever Changing**. I believe in a "startup mentality" where we are **Here to Win** through speed and superior knowledge, while always remembering to **Extend Ourselves** through inclusive teamwork and support.',
        keyPoints: [
            'Customer Obsessed: Placing the customer at the top of the pyramid and moving with speed to build trust.',
            'Owners at Heart: Taking personal responsibility and leaving things better than found.',
            'Curious and Ever Changing: Embracing innovation (like AI) and adapting to new technologies.',
            'Here to Win: Driving results with a "startup mentality" and quick decision-making.',
            'We Extend Ourselves: Creating a positive, inclusive, and fun work environment through collaboration.',
        ],
        code: `// Pseudocode: 1-Way vs 2-Way Door Decision Framework
function makeDecision(decision) {
  if (decision.isReversible) {
    // 2-Way Door: Decide fast, experiment, learn.
    return executeFast(decision);
  } else {
    // 1-Way Door: High impact, hard to reverse.
    return deliberateCarefully(decision);
  }
}

# Leadership Strategy:
# - Empower team to handle 2-Way doors independently.
# - My role as manager: focus on 1-Way doors and strategic alignment.`,
        qa: [
            {
                q: 'Q: How do you handle a high-performing team member who suddenly starts underperforming?',
                a: `**Situation (Amex):** A key team member who was previously high-performing started missing deadlines and delivering below their usual capability.
                
**Task:** Identify the root cause and support the individual to ensure both their career satisfaction and the team's success.

**Action:** During our regular 1-1, I focused on empathy and understanding their point of view. I discovered they were feeling stagnant and wanted to learn new technologies and move to a different area. Instead of forcing them to stay, I prioritized their growth. I spoke to other teams, advocated for them, and referred them to a internal role that matched their new interests.

**Result:** The member became a highly successful and happy contributor in their new team. Ultimately, the company retained a talented employee, and the team culture of "human-first" management was strengthened.`,
                star: true,
            },
            {
                q: 'Q: How do you foster a "Safe to Fail" environment while still delivering results?',
                a: `I tell my team that they shouldn't hesitate to try new things. Innovation requires it.

1. **Clear Boundaries:** We distinguish between "safe-to-fail" experiments (2-way doors) and mission-critical systems.
2. **Constructive Feedback:** We treat feedback as encouragement for the team's success, not as a personal critique.
3. **Structured Support:** I ensure that when we try something new, we have a rollback plan or a learning path.
4. **Human Relationships:** By paying attention to individual needs and points of view, I build the trust necessary for people to take calculated risks without fear.`,
            },
        ],
    },
];

